import { useState } from 'react';
import { Switch } from '@headlessui/react';
import { cn } from '../../utils';

type ValueCallback = (value: boolean) => void;

type LabelDescriptionProps = {
    title: string;
    description?: string | null;
};

type ToggleProps = {
    defaultValue?: boolean;
    disabled?: boolean;
    label?: LabelDescriptionProps | string | null;
    labelLeft?: boolean;
    onChange: ValueCallback;
};

export default function Toggle({
    defaultValue = false,
    disabled = false,
    label = null,
    labelLeft = false,
    onChange,
}: ToggleProps) {
    const [isEngaged, setIsEngaged] = useState(defaultValue);

    return (
        <Switch.Group
            as="div"
            data-testid="Toggle"
            className={cn(`alli-flex alli-items-start alli-space-x-3`, {
                'alli-justify-between': labelLeft,
            })}>
            {label && labelLeft && (
                <span
                    className={cn(
                        typeof label === 'object' &&
                            label?.description &&
                            'alli-mr-3',
                    )}>
                    <Label label={label} />
                </span>
            )}
            <Switch
                checked={isEngaged}
                onChange={value => {
                    if (disabled) {
                        return;
                    }
                    onChange(value);
                    setIsEngaged(!isEngaged);
                }}
                className={cn(
                    getColor(isEngaged, disabled),
                    'alli-relative alli-inline-flex alli-h-6 alli-w-11 alli-flex-shrink-0 alli-cursor-pointer alli-rounded-full alli-border-2 alli-border-transparent alli-transition-colors alli-duration-200 alli-ease-in-out focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-blue-600 focus:alli-ring-offset-2',
                )}>
                <span
                    aria-hidden="true"
                    className={cn(
                        isEngaged ? 'alli-translate-x-5' : 'alli-translate-x-0',
                        'alli-pointer-events-none alli-inline-block alli-h-5 alli-w-5 alli-transform alli-rounded-full alli-bg-white alli-shadow alli-ring-0 alli-transition alli-duration-200 alli-ease-in-out',
                    )}
                />
            </Switch>
            {label && !labelLeft && (
                <span className="alli-ml-3">
                    <Label label={label} />
                </span>
            )}
        </Switch.Group>
    );
}

function getColor(engaged: boolean, disabled: boolean) {
    return colors[engaged ? 'engaged' : 'disengaged'][
        disabled ? 'disabled' : 'active'
    ];
}

const colors = {
    engaged: {
        disabled: 'alli-cursor-not-allowed alli-bg-opacity-40 alli-bg-blue-600',
        active: 'alli-bg-blue-600 hover:alli-bg-blue-700 alli-cursor-pointer',
    },
    disengaged: {
        disabled: 'alli-cursor-not-allowed alli-bg-opacity-40 alli-bg-gray-600',
        active: 'alli-bg-gray-600 hover:alli-bg-gray-700 alli-cursor-pointer',
    },
};

function Label({ label }: { label: string | LabelDescriptionProps }) {
    return typeof label === 'string' ? (
        <LabelText>{label}</LabelText>
    ) : (
        <LabelDescription title={label.title} description={label.description} />
    );
}

function LabelText({ children }: { children: string }) {
    return (
        <Switch.Label as="span" className="alli-text-sm">
            <span className="alli-font-medium alli-text-blue-gray-700">
                {children}
            </span>
        </Switch.Label>
    );
}

function LabelDescription({
    title,
    description = null,
}: LabelDescriptionProps) {
    return (
        <span className="alli-flex alli-flex-grow alli-flex-col">
            <Switch.Label
                as="span"
                className="alli-text-sm alli-font-medium alli-leading-6 alli-text-blue-gray-700"
                passive>
                {title}
            </Switch.Label>
            {description && (
                <Switch.Description
                    as="span"
                    className="alli-text-sm alli-text-gray-750">
                    {description}
                </Switch.Description>
            )}
        </span>
    );
}

// Text for the Label styling probably still needs some attention....
// but the rest seems good and added the upgrade guide to try to capture things when it is fresh

// Maybe Classnames????? or cslx?
